"use client";

import { createContext, useContext, useState, useEffect, useCallback } from "react";

interface LoadingContextType {
  /** Number of active loading tasks. Loader is visible when > 0 or not hydrated. */
  appLoadingTasks: number;
  /** Register a new loading task (increments counter). */
  startLoadingTask: () => void;
  /** Release a loading task (decrements counter, floored at 0). */
  stopLoadingTask: () => void;
  /** Whether the initial client-side hydration is complete. */
  isHydrated: boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [appLoadingTasks, setAppLoadingTasks] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);

  const startLoadingTask = useCallback(() => {
    setAppLoadingTasks((prev) => prev + 1);
  }, []);

  const stopLoadingTask = useCallback(() => {
    setAppLoadingTasks((prev) => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    // Mark hydration complete after the first render cycle.
    // The loader stays alive via appLoadingTasks registered by child components
    // (e.g. ParallaxVisuals holds a task until the bg video fires canplay).
    const timer = setTimeout(() => {
      setIsHydrated(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  return (
    <LoadingContext.Provider value={{ appLoadingTasks, startLoadingTask, stopLoadingTask, isHydrated }}>
      {children}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
}
